import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import HotelMarketInsightTablesCellsTdHotelName from 'lumen/components/hotel/market-insight/tables/cells/td-hotel-name';
import HotelMarketInsightTablesCellsTdStars from 'lumen/components/hotel/market-insight/tables/cells/td-stars';
import HotelRatesTablesCellsThHotel from 'lumen/components/hotel/rates/tables/cells/th-hotel';
import type { MarketInsightHotel } from 'lumen/utils/market-insight/market-insight-helpers';

const COLUMNS = {
  HOTEL: 'hotel',
  REVIEW: 'review',
  STARS: 'stars',
  SIMILARITY: 'similarity',
};

interface HotelMarketInsightTablesTableSmartCompsetDetailsSignature {
  Args: {
    model: {
      hotel: MarketInsightHotel;
      review: number;
      similarity: string;
    }[];
    setHighlightedMarker: (hotelId: string, isHovered: boolean) => void;
  };
}

export default class HotelMarketInsightTablesTableSmartCompsetDetails extends Component<HotelMarketInsightTablesTableSmartCompsetDetailsSignature> {
  @service declare intl: Services['intl'];

  initialSorts = [
    {
      valuePath: COLUMNS.REVIEW,
      isAscending: false,
    },
  ];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    return [
      {
        name: this.intl.t('general.hotel.plural', { count: 1 }),
        valuePath: `${COLUMNS.HOTEL}.value`,
        displayValuePath: `${COLUMNS.HOTEL}.formattedValue`,
        width: 120,
        isSortable: false,
        tdComponent: HotelMarketInsightTablesCellsTdHotelName,
        textAlign: 'left' as const,
        setHighlightedMarker: this.args.setHighlightedMarker,
      },
      {
        name: this.intl.t('market_insight.review'),
        valuePath: COLUMNS.REVIEW,
        textAlign: 'center' as const,
        width: 90,
      },
      {
        name: this.intl.t('market_insight.stars'),
        displayValuePath: `${COLUMNS.STARS}.formattedValue`,
        valuePath: `${COLUMNS.STARS}.value`,
        textAlign: 'center' as const,
        width: 75,
        tdComponent: HotelMarketInsightTablesCellsTdStars,
      },
      {
        name: this.intl.t('market_insight.similarity'),
        valuePath: COLUMNS.SIMILARITY,
        textAlign: 'center' as const,
        width: 105,
        thComponent: HotelRatesTablesCellsThHotel,
        infoTextTitle: this.intl.t('market_insight.similarity_score'),
        infoText: this.intl.t(
          'market_insight.kpi_explanation.similarity_score',
        ),
        hasMargin: false,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    if (!this.args.model) {
      return [];
    }

    return this.args.model.map(({ hotel, review, similarity }) => {
      return {
        className: hotel?.isOwnHotel ? 'own-hotel-row' : '',
        [COLUMNS.HOTEL]: {
          value: hotel,
          formattedValue: hotel.name,
        },
        [COLUMNS.REVIEW]: review,
        [COLUMNS.STARS]: {
          value: hotel.stars,
          formattedValue: hotel.formattedStars,
        },
        [COLUMNS.SIMILARITY]: similarity,
      };
    });
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @enableAutoSortsUpdate={{true}}
      @fillMode="first-column"
      @initialSorts={{this.initialSorts}}
      @removeTableBorder={{true}}
      @rows={{this.rows}}
      @widthConstraint="eq-container"
      class="smart-compset-table"
    />
  </template>
}
